import { Component, OnInit } from '@angular/core';
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { validEmailValidator } from '@app/components/commons/forms/formly-validators';
import {
  getCertificateActivationContext,
  shouldLogoutForEmptyCertificate,
} from '@app/helpers/certificate.helpers';
import { OtpService } from '@app/services/authenticators/otp.service';
import {
  CertificateInfo,
  IssuerDN,
  SubjectDN,
  SubjectDnMap,
  SubjectDnMapKeys,
} from '@app/store/models/certificate.model';
import { selectCertificateInfo } from '@app/store/selectors/certificate.selector';
import { GlobalState } from '@app/store/store';
import { DropdownItem } from '@infocert/ng-ui-kit/models';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { isArray, pickBy } from 'lodash-es';
import moment from 'moment';
import { LocalStorageService } from 'ngx-webstorage';
import { forkJoin, lastValueFrom, map, Observable, tap } from 'rxjs';
import { CertificateService } from '../../../services/certificates/certificate.service';
import { SessionService } from '../../../services/session/session.service';
import { UserCertificate } from './../../../store/models/certificate.model';
@UntilDestroy()
@Component({
  selector: 'app-certificate',
  templateUrl: './certificate.component.html',
  styleUrls: ['./certificate.component.scss'],
  standalone: false,
})
export class CertificatePageComponent implements OnInit {
  certInfo: CertificateInfo | undefined;
  certificate: UserCertificate | undefined;
  showDownloads: boolean = false;
  showEmailChangeSuccess = false;

  isDropdownOpen: boolean = false;
  dropdownItems$: Observable<DropdownItem[]>;
  items: DropdownItem[] = [];
  isExpiring: boolean;

  certStateMessage: string = '';
  expirationDate: string = '';
  aliasCertificate: string = '';
  domain: string = '';
  email: string = '';

  loading: boolean = false;
  changeEmailAttempted = false;
  contentReady = false;
  pageTitle = '';

  toggleDropdown() {
    this.isDropdownOpen = !this.isDropdownOpen;
  }

  constructor(
    private store: Store<GlobalState>,
    private certificateService: CertificateService,
    private sessionService: SessionService,
    private _translateService: TranslateService,
    private localStorageService: LocalStorageService,
    private otpService: OtpService
  ) {}

  ngOnInit(): void {
    this.store
      .select(selectCertificateInfo)
      .pipe(
        untilDestroyed(this),
        tap(data => {
          this.certInfo = data;
        })
      )
      .subscribe();

    // Getter per aggiornare i dati del certificato
    this.getCertificate();

    this.dropdownItems$ = this._translateService
      .stream([
        'CERT_PAGE.SELECT_FORMAT',
        'CERT_PAGE.DOWNLOAD_CERT_DER',
        'CERT_PAGE.DOWNLOAD_CERT_B64',
        'CERT_PAGE.DOWNLOAD_CERT_PEM',
      ])
      .pipe(
        map(res => [
          {
            label: res['CERT_PAGE.SELECT_FORMAT'],
            disabled: true,
            role: 'option',
            'aria-disabled': 'true',
          } as DropdownItem,
          {
            label: res['CERT_PAGE.DOWNLOAD_CERT_DER'],
            value: 'der',
            role: 'option',
          },
          {
            label: res['CERT_PAGE.DOWNLOAD_CERT_B64'],
            value: 'b64',
            role: 'option',
          },
          {
            label: res['CERT_PAGE.DOWNLOAD_CERT_PEM'],
            value: 'pem',
            role: 'option',
          },
        ])
      );

    forkJoin({
      title: this._translateService.get('HOMEPAGE.CERT.TITLE'),
      readiness: this._translateService.get('CERT_PAGE.DETAILS.TITLE'),
    }).subscribe(({ title }) => {
      this.pageTitle = title;
      requestAnimationFrame(() => (this.contentReady = true));
    });

    // Dopo un tentativo di submit, nasconde i validator mentre l'utente digita.
    this.emailForm.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
      if (this.changeEmailAttempted) {
        this.changeEmailAttempted = false;
      }
    });
  }

  public changeEmailModalVisible = false;

  get downloadUrl(): string {
    const downloadUrl = this.certificateService.getDownloadUrl();
    console.log(
      '🚀 ~ CertificatePageComponent ~ getdownloadUrl ~ downloadUrl:',
      downloadUrl
    );

    return downloadUrl;
  }
  hasValue(value: any): boolean {
    if (!value) return false;

    if (isArray(value)) {
      if (value.length === 0) return false;
    }

    return true;
  }

  get subjectDN(): Partial<SubjectDN> | undefined {
    return (
      this.certInfo?.subjectDN &&
      pickBy(this.certInfo.subjectDN, (value, prop) => this.hasValue(value))
    );
  }

  get subjectDnMap(): Partial<SubjectDnMap> | undefined {
    return (
      this.certInfo?.subjectDnMap &&
      pickBy(this.certInfo.subjectDnMap, (value, prop) => this.hasValue(value))
    );
  }

  get issuerDN(): Partial<IssuerDN> | undefined {
    return (
      this.certInfo?.issuerDN &&
      pickBy(this.certInfo.issuerDN, (value, prop) => this.hasValue(value))
    );
  }

  get certificateId(): string {
    return this.certificateService.certID;
  }

  getCertificate(): void {
    console.log('email' + this.email);

    this.certificateService.getCertificate().subscribe({
      next: async certificate => {
        const activationContext = getCertificateActivationContext(
          this.localStorageService
        );

        if (shouldLogoutForEmptyCertificate(certificate, activationContext)) {
          void this.sessionService.logout();
          return;
        }

        this.certificate = certificate;
        this.email = this.certificate.email;
        this.certStateMessage = certificate.active
          ? 'COMMON.ACTIVE'
          : 'COMMON.INACTIVE';
        this.aliasCertificate = certificate.aliasCertificate;
        this.domain = certificate.domain;

        const validityTo = certificate.validityTo?.trim();
        this.expirationDate = validityTo
          ? moment(validityTo.substring(0, 10)).format('DD/MM/YYYY')
          : '';

        if (validityTo) {
          const expiration = moment(validityTo.substring(0, 10));
          const today = moment().startOf('day');
          const daysDiff = expiration.diff(today, 'days');
          this.isExpiring = daysDiff <= 89 && daysDiff >= 0;
        } else {
          this.isExpiring = false;
        }
        console.log('isExpiring:' + this.isExpiring);
      },
      error: error => {
        // Se va in errore il get del certificato allora si forza il logout
        this.sessionService.logoutNoSession();
        console.error(error);
      },
      complete: () => {},
    });
  }

  // funzione per mantenere l'ordine dell'oggetto
  asIsOrder() {
    return 1;
  }

  showDropdown() {
    if (this.showDownloads) {
      this.showDownloads = false;
    } else {
      this.showDownloads = true;
    }
  }

  downloadCert(downloadFormat: string) {
    this.certificateService.downloadCertificate(downloadFormat);
  }

  onSelectDropdown(value: any) {
    console.log(value);
    this.downloadCert(value);
  }

  //INIZIO PROCESSO CAMBIO EMAIL

  //invio otp
  sendOtp() {
    this.otpService.generateCode().subscribe({
      next: async response => {
        // Se tutto va bene
      },
      error: error => {
        console.error(error);
      },
      complete: () => {},
    });
  }

  //apro la modale
  openModal() {
    this.changeEmailAttempted = false;
    this.emailForm.patchValue(
      {
        emailField: this.email || '',
        otp: '',
      },
      { emitEvent: false }
    );
    this.emailField.markAsPristine();
    this.emailField.markAsUntouched();
    this.otp.markAsPristine();
    this.otp.markAsUntouched();
    this.sendOtp();
    this.changeEmailModalVisible = true;
  }

  onToggleChangeEmailModal(visible: boolean): void {
    this.changeEmailModalVisible = visible;
    if (!visible) {
      this.closeModal();
    }
  }

  //form del cambio email

  emailField = new UntypedFormControl('', [
    Validators.required,
    validEmailValidator,
  ]);

  otp = new UntypedFormControl('', [
    Validators.required,
    Validators.minLength(6),
    Validators.maxLength(8),
  ]);

  emailForm = new UntypedFormGroup({
    emailField: this.emailField,
    otp: this.otp,
  });

  get fEmail() {
    return this.emailForm.controls;
  }

  get emailFormErrors() {
    const errors = this.emailForm.errors;
    return errors;
  }

  //chiudo la modale
  closeModal() {
    this.changeEmailModalVisible = false;
    this.clearOtpForm();
  }

  //ripulisco le form quando chiudo la modale
  clearOtpForm() {
    this.otpService.resetOtpInputVal(true);
    this.changeEmailAttempted = false;
    this.emailField = new UntypedFormControl(' ', [Validators.required]);
    this.otp = new UntypedFormControl('', [
      Validators.required,
      Validators.minLength(6),
      Validators.maxLength(8),
    ]);

    this.emailForm = new UntypedFormGroup({
      emailField: this.emailField,
      otp: this.otp,
    });
  }

  // cambio email
  onChangeEmail() {
    this.loading = true;
    if (this.emailForm.invalid) {
      this.changeEmailAttempted = true;
      this.emailForm.markAllAsTouched();
      this.loading = false;
      return;
    }

    this.certificateService
      //@ts-ignore
      .changeEmail(
        this.fEmail['emailField'].value.trim(),
        this.fEmail['otp'].value.trim()
      )
      .subscribe({
        next: async response => {
          this.loading = false;

          // Se tutto va bene
          this.clearOtpForm();

          this.closeModal();
        },
        error: error => {
          console.error(error);
          this.loading = false;
        },
        complete: () => {
          this.loading = false;
          this.refreshSessionData();
          this.showEmailChangeSuccess = true;
          //setto la variabile che mostra il banner di successo a false dopo 5 secondi
          setTimeout(() => {
            this.showEmailChangeSuccess = false;
          }, 10000);
        },
      });
  }

  //refresh della session
  refreshSessionData() {
    this.sessionService.getUserSession().then(async session => {
      this.localStorageService.store('session', session);
      const certificates = await lastValueFrom(
        this.certificateService.getCertificateList()
      );
      this.certificateService.certID = certificates?.[0].certId;
      const certificate = await lastValueFrom(
        this.certificateService.getCertificate()
      );
      this.localStorageService.store('certificate', certificate);
      this.email = certificate.email;
    });
  }

  // Funzione per verificare se la chiave è contenuta in SubjectDnMap
  isContainedKey(key: string): key is keyof SubjectDnMap {
    // Array delle chiavi contenute estratte dall'enum
    const validKeys: Array<string> = Object.values(SubjectDnMapKeys);

    // Verifica se la chiave è presente nell'array di chiavi valide
    return validKeys.includes(key);
  }

  // Funzione per ottenere la chiave di traduzione di SubjectDnMap.
  getTranslationKey(key: string): string {
    // Ritorna la chiave tradotta se contenuta, altrimenti la chiave originale
    return this.isContainedKey(key)
      ? `CERT_PAGE.LABEL.${key.toUpperCase()}`
      : key;
  }
}
